/**
 * 27/10/2026 — the paste review in one card (user: "why I cant paste? … make it intuitive"):
 * ready (green), N to look at (amber) or can't paste yet (red), what comes ("9 containers ·
 * 45 drops"), why Paste is off when it is, the sections that stay the target's (and why, behind
 * ⓘ), and the rows that need the person — full, with their pickers; answered ones stay, marked.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle, CheckCircle2, CircleDot, Info } from 'lucide-react';
import type { GroupCopyReview, GroupCopySectionRow } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';
import type { MatchKind } from '@/features/drop-generator/components/group-copy/dialog/model/paste-summary';
import type { PickKind } from '@/features/drop-generator/components/group-copy/dialog/picks/use-pick-kinds';
import { cn } from '@/lib/utils';

type Tone = 'ready' | 'needs' | 'blocked';

interface Props {
  review: GroupCopyReview;
  kinds: PickKind[];
  inCard: (kind: MatchKind, key: string) => boolean;
}

const CARD: Record<Tone, string> = {
  ready: 'border-success/30 bg-success/[0.04]',
  needs: 'border-warning/40 bg-warning/[0.05]',
  blocked: 'border-destructive/40 bg-destructive/[0.04]',
};
const ICON = { ready: CheckCircle2, needs: CircleDot, blocked: AlertTriangle };
const ICON_TONE: Record<Tone, string> = { ready: 'text-success', needs: 'text-warning', blocked: 'text-destructive' };

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function headline(tone: Tone, needs: number, review: GroupCopyReview, asked: boolean): { title: string; sub: string } {
  const country = review.target.countryName;
  if (tone === 'blocked') return { title: `Can't paste into ${country} yet`, sub: 'Fix what is below first — nothing has been written.' };
  if (tone === 'needs') return { title: `${needs} to look at`, sub: `Everything else found its match in ${country} by itself.` };
  return { title: 'Ready to paste', sub: asked ? 'Everything is answered.' : `Everything found its match in ${country} by itself.` };
}

const Head: FC<{ tone: Tone; needs: number; review: GroupCopyReview; asked: boolean }> = ({ tone, needs, review, asked }) => {
  const Icon = ICON[tone];
  const { title, sub } = headline(tone, needs, review, asked);
  const taken = review.containers.filter((c) => c.take);
  const drops = taken.reduce((n, c) => n + c.slots, 0);
  return (
    <div className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-1 px-5 py-3.5">
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0 transition-colors duration-300', ICON_TONE[tone])} aria-hidden />
      <div className="min-w-0 flex-1 basis-48">
        <p className="text-[15px] font-semibold leading-tight">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>
      </div>
      <p className="shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
        {plural(taken.length, 'container')} · {plural(drops, 'drop')}
      </p>
    </div>
  );
};

const Blockers: FC<{ blockers: string[] }> = ({ blockers }) =>
  blockers.length === 0 ? null : (
    <ul aria-label="Why Paste is off" className="flex flex-col gap-1.5 border-t border-destructive/20 px-5 py-2.5">
      {blockers.map((b) => (
        <li key={b} className="flex items-start gap-1.5 text-xs text-destructive">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {b}
        </li>
      ))}
    </ul>
  );

const Held: FC<{ rows: GroupCopySectionRow[]; country: string }> = ({ rows, country }) =>
  rows.length === 0 ? null : (
    <ul aria-label="Stays as it is" className="flex flex-col gap-1 border-t border-border/50 px-5 py-2.5">
      {rows.map((s) => (
        <li key={s.key} className="flex items-center gap-1.5 text-xs">
          <Info className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          <span><span className="font-medium">{s.label}</span> stays {country}&apos;s</span>
          <HintDot label={s.held!} />
        </li>
      ))}
    </ul>
  );

const Attention: FC<{ kind: PickKind; inCard: Props['inCard'] }> = ({ kind, inCard }) => {
  const rows = kind.entries.filter((e) => inCard(kind.kind, e.key));
  if (rows.length === 0) return null;
  return (
    <div className="border-t border-border/50 pb-1">
      <p className="px-5 pt-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{kind.title}</p>
      {rows.map((e) => e.render(false))}
    </div>
  );
};

export const StatusCard: FC<Props> = ({ review, kinds, inCard }) => {
  const needs = kinds.reduce((n, k) => n + k.entries.filter((e) => e.needs).length, 0);
  const tone: Tone = review.blockers.length > 0 ? 'blocked' : needs > 0 ? 'needs' : 'ready';
  const asked = kinds.some((k) => k.entries.some((e) => inCard(k.kind, e.key)));
  return (
    <section aria-label="Paste status" className={cn('min-w-0 overflow-hidden rounded-xl border transition-colors duration-300', CARD[tone])}>
      <Head tone={tone} needs={needs} review={review} asked={asked} />
      <Blockers blockers={review.blockers} />
      <Held rows={review.sections.filter((s) => s.held)} country={review.target.countryName} />
      {kinds.map((k) => <Attention key={k.kind} kind={k} inCard={inCard} />)}
    </section>
  );
};
